import { ref } from 'vue'
import { API_BASE_URL, WEBSITE_BASE_URL } from '@/api/client'

const apiBaseUrl = API_BASE_URL
const websiteBaseUrl = WEBSITE_BASE_URL

/**
 * Composable for generating locale-aware preview URLs for the website-generator
 * Handles the i18n routing compatibility between admin frontend and website-generator
 */
export function usePreviewUrl() {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Generate a preview URL with correct locale prefix for website-generator
   * @param projectSlug - The project slug from the admin
   * @param pageSlug - The page slug to preview
   * @returns Promise<string> - The locale-prefixed preview URL
   */
  const generatePreviewUrl = async (projectSlug: string, pageSlug: string): Promise<string> => {
    isLoading.value = true
    error.value = null

    try {
      // Fetch project config to get default locale for website-generator routing
      const response = await fetch(`${apiBaseUrl}/api/projects/${projectSlug}/website?locale=en`)

      if (!response.ok) {
        throw new Error(`Failed to fetch project config: ${response.status} ${response.statusText}`)
      }

      const config = await response.json()

      // Extract default locale from the API response
      const defaultLocale = config.data?.content?.[0]?.language || 'en'

      // Generate i18n-compatible preview URL with locale prefix
      return `${websiteBaseUrl}/${defaultLocale}/${pageSlug}`

    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error occurred'
      console.error('Failed to generate preview URL:', err)

      // Fallback to default locale if API call fails
      return `${websiteBaseUrl}/en/${pageSlug}`
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Open a preview URL in a new tab with proper error handling
   * @param projectSlug - The project slug
   * @param pageSlug - The page slug
   */
  const openPreview = async (projectSlug: string, pageSlug: string): Promise<void> => {
    const previewUrl = await generatePreviewUrl(projectSlug, pageSlug)
    window.open(previewUrl, '_blank')
  }

  return {
    isLoading,
    error,
    generatePreviewUrl,
    openPreview
  }
}